import { Button } from '@material-ui/core';
import { useConfirm } from 'material-ui-confirm';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import '../../index.scss'
import EntryFileField from '../../molecules/entry-file-field.component';
import EntryRadioField from '../../molecules/entry-radio-field.component';
import EntrySelectField from '../../molecules/entry-select-field.component';
import EntryTextField from '../../molecules/entry-text-field.component';
import EntryTitle from '../../molecules/entry-title.component';
import LoadingScreen from '../../molecules/loading-screen.component';
import { RecruitEntryService } from '../../services/recruit-entry/recruit-entry.service';
import { getJobTypeValues, JobType } from '../../types';
import { EntryType, PickerOptionItem } from '../types';
import EntryContext from './entry-context';
import { expandPicklist } from './entry-utils';

interface EntryCreatePageProp {
  entryType: EntryType,
}
export default function EntryCreatePage({
  entryType,
}: EntryCreatePageProp) {
  const history = useHistory();
  // @ts-ignore
  const { jobType } = useParams();
  const confirm = useConfirm();
  const { data, setData, picklist, setPicklist } = useContext(EntryContext);
  const { register, handleSubmit, errors, setError, clearErrors } = useForm();

  const [loading, setLoading] = useState(false);
  const [genderTypes, setGenderTypes] = useState<PickerOptionItem[]>([]);
  const [howToKnowTypes, setHowToKnowTypes] = useState<PickerOptionItem[]>([]);
  const [desiredJobTypes, setDesiredJobTypes] = useState<PickerOptionItem[]>([]);
  
  useEffect(() => {
    let supress = false;

    (async () => {
      if(!picklist || picklist.length === 0) {
        setLoading(true);
        
        const result = await new RecruitEntryService().getPicklist();

        setPicklist(result);
      }
      if(!supress) {
        const { genderTypes, howToKnowTypes, desiredJobTypes } = expandPicklist(picklist!);

        setGenderTypes(genderTypes);
        setHowToKnowTypes(howToKnowTypes);
        
        const jobTypeValues = getJobTypeValues();
        // 画面に出して良いのは申し込める４種類のみ
        setDesiredJobTypes(desiredJobTypes.filter(o => jobTypeValues.includes(o.value)));

        setLoading(false);
      }

    })();

    const cleanup = () => {
      supress = true;
    }
    return cleanup;

  }, []);

  const onSubmit = async (data: any) => {
    setData({
      ...data,
      requestType: entryType,
    });
    history.push(`/entry/${entryType}/${jobType}/confirm`);
  }

  const onInvalid = (errors: any) => {
  }

  const onCancel = () => {
    confirm({
      title: 'キャンセル確認',
      description: '入力中の内容が失われますが、よろしいですか？',
    })
    .then(() => {
      history.replace('/');
    });
  }
  return (
    <>
      <div className="entry">
        {EntryTitle({entryType})}

        <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)}>
          {EntrySelectField({
            name: 'desiredJobType',
            label: '希望職種',
            value: jobType ?? data?.desiredJobType,
            errors, register, required: true,
            optionItems: desiredJobTypes,
          })}

          {EntryTextField({
            name: 'fullName',
            label: '氏名',
            value: data?.fullName,
            errors, register, required: true,
          })}

          {EntryTextField({
            name: 'fullNameKana',
            label: 'フリガナ',
            value: data?.fullNameKana,
            errors, register, required: true,
            pattern: /^[ァ-ンヴー]*$/,
            patternError: '全角カタカナで入力してください'
          })}

          {EntryTextField({
            name: 'phone',
            label: '電話番号',
            value: data?.phone,
            errors, register, required: true,
            pattern: /^([0-9])+$/,
            patternError: '数字のみでご入力ください',
            placeholder: '00000000000',
          })}

          {EntryTextField({
            name: 'email',
            label: 'メールアドレス',
            value: data?.email,
            errors, register, required: true,
            pattern: /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/,
            patternError: 'メールアドレスの形式が正しくありません',
          })}

          {EntryTextField({
            name: 'address',
            label: '住所',
            value: data?.address,
            errors, register, required: true,
            visible: entryType === 'recruit',
          })}

          {EntryTextField({
            name: 'age',
            label: '年齢',
            value: data?.age,
            errors, register, required: true,
            pattern: /^([0-9])+$/,
            patternError: '数字のみでご入力ください',
            visible: entryType === 'recruit',
          })}

          {EntryRadioField({
            name: 'gender',
            label: '性別',
            value: data?.gender,
            errors, register, required: true,
            visible: entryType === 'recruit',
            optionItems: genderTypes,
          })}

          {EntryFileField({
            name: 'photo',
            label: '顔写真(JPG/PNG)',
            value: data?.photo,
            fileName: data?.photoName,
            errors, register, required: true,
            setError, clearErrors,
            visible: entryType === 'recruit',
          })}

          {EntrySelectField({
            name: 'howToKnow',
            label: 'HOSHINOを知ったきっかけ',
            value: data?.howToKnow,
            errors, register, required: true,
            optionItems: howToKnowTypes,
          })}

          {EntryTextField({
            name: 'career',
            label: '最終学歴',
            value: data?.career,
            rows: 5,
            errors, register, required: true,
            visible: entryType === 'recruit',
            placeholder: 'YYYY年　国立　〇〇大学　卒業'
          })}

          {EntryTextField({
            name: 'qualified',
            label: '資格',
            value: data?.qualified,
            rows: 5,
            errors, register, required: true,
            visible: entryType === 'recruit',
          })}

          {EntryTextField({
            name: 'motivation',
            label: '志望動機',
            placeholder: 'なぜHOSHINOで働くことを希望しますか。',
            value: data?.motivation,
            rows: 5,
            errors, register, required: true,
            visible: entryType === 'recruit',
          })}

          {EntryTextField({
            name: 'achievement',
            label: '功績',
            placeholder: 'あなたにとっての人生最大のピンチとチャレンジをお聞かせ頂けますか。',
            value: data?.achievement,
            rows: 5,
            errors, register, required: true,
            visible: entryType === 'recruit',
          })}

          {EntryTextField({
            name: 'commitment',
            label: 'コミットメント',
            placeholder: 'あなたがHOSHINOでチャレンジしたいこと、そしてHOSHINOの何を変えて頂けますか。',
            value: data?.commitment,
            rows: 5,
            errors, register, required: true,
            visible: entryType === 'recruit',
          })}

          <br />

          {EntryFileField({
            name: 'jobHistory',
            label: '職務経歴書(PDF)',
            value: data?.jobHistory,
            fileName: data?.jobHistoryName,
            errors, register, required: true,
            setError, clearErrors,
            visible: entryType === 'recruit',
            preview: false,
            acceptContentTypes: 'application/pdf'
          })}

          <div className="entry-buttons">
            <Button
              type="submit"
              className="entry-button"
              variant="contained"
              color="primary"
            >
              確認
            </Button>
            <Button
              type="button"
              className="entry-button"
              variant="contained"
              onClick={onCancel}
            >
              キャンセル
            </Button>
          </div>
        </form>
      </div>
      {LoadingScreen({ loading })}
    </>
  );
}
